import type {
  DriverAttachment,
  DriverDetails,
  DriverStatus,
  NamedRef,
} from "../domain";

/** A stored driver: `DriverDetails` minus the attachments, which the mock
 *  keeps per id and joins in on read (as the backend returns them). */
export type DriverFixture = Omit<DriverDetails, "attachments">;

const AMANA: NamedRef = {
  id: "company-amana",
  nameEn: "Amana Jeddah Company",
  nameAr: "شركة أمانة جدة",
};
const JEDDAH: NamedRef = {
  id: "branch-jeddah",
  nameEn: "Jeddah Branch",
  nameAr: "فرع جدة",
};
const RIYADH: NamedRef = {
  id: "branch-riyadh",
  nameEn: "Riyadh Branch",
  nameAr: "فرع الرياض",
};

function driver(
  seq: number,
  nameEn: string,
  nameAr: string,
  username: string,
  licenseExpiryDate: string,
  status: DriverStatus,
  isAssigned: boolean,
  branch: NamedRef | null = JEDDAH,
): DriverFixture {
  const suffix = String(seq).padStart(3, "0");
  return {
    id: `driver-${suffix}`,
    nameEn,
    nameAr,
    avatarUrl: null,
    nationalId: String(1098765430 + seq),
    licenseNumber: `LIC-SA-2021-${suffix}`,
    licenseExpiryDate,
    status,
    isAssigned,
    username,
    email: `${username}@nasam.sa`,
    mobileNumber1: `+9665834511${String(seq).padStart(2, "0")}`,
    // Optional fields go empty on a fixed cadence so N/A shows up in the data.
    mobileNumber2:
      seq % 2 === 0 ? `+9665511234${String(seq).padStart(2, "0")}` : null,
    rfid: seq % 5 === 0 ? null : `RFID-${String(40000 + seq * 37)}`,
    licenseIssueDate: "2021-01-15",
    company: branch === null ? null : AMANA,
    branch,
  };
}

/** 14 drivers (two pages at the default 10): four hold licenses that expired
 *  before 2026-09, the rest run to 2027 or later. 8 active, 4 inactive and
 *  2 pending activation. */
export const driverFixtures: readonly DriverFixture[] = [
  driver(
    1,
    "Ali bin Abi Talib",
    "علي بن أبي طالب",
    "a.talib",
    "2027-01-18",
    "ACTIVE",
    true,
  ),
  driver(
    2,
    "Mohammed bin Abdullah Al-Ghamdi",
    "محمد بن عبدالله الغامدي",
    "m.alghamdi",
    "2027-01-15",
    "INACTIVE",
    false,
  ),
  driver(
    3,
    "Hussein bin Ali",
    "حسين بن علي",
    "h.ali",
    "2026-07-15",
    "ACTIVE",
    true,
  ),
  driver(
    4,
    "Yousef Ahmed Mohammed",
    "يوسف احمد محمد",
    "y.ahmed",
    "2027-01-16",
    "INACTIVE",
    false,
  ),
  driver(
    5,
    "Al-Hussein bin Ali",
    "الحسين بن علي",
    "alhussein.ali",
    "2027-01-17",
    "PENDING_ACTIVATION",
    false,
  ),
  driver(
    6,
    "Mohammed bin Abdullah Al-Qahtani",
    "محمد بن عبدالله القحطاني",
    "m.alqahtani",
    "2026-05-15",
    "ACTIVE",
    true,
  ),
  driver(
    7,
    "Mohammed Ahmed Ali",
    "محمد احمد علي",
    "m.ahmed",
    "2026-01-15",
    "INACTIVE",
    false,
  ),
  driver(
    8,
    "Ahmed Mohammed Fahd",
    "احمد محمد فهد",
    "a.mohamed",
    "2028-03-01",
    "ACTIVE",
    true,
    RIYADH,
  ),
  driver(
    9,
    "Khalid Saad Al-Otaibi",
    "خالد سعد العتيبي",
    "k.alotaibi",
    "2029-06-30",
    "ACTIVE",
    true,
    RIYADH,
  ),
  driver(
    10,
    "Fahd Nasser Al-Dosari",
    "فهد ناصر الدوسري",
    "f.aldosari",
    "2025-11-20",
    "ACTIVE",
    false,
  ),
  driver(
    11,
    "Omar Saleh Al-Harbi",
    "عمر صالح الحربي",
    "o.alharbi",
    "2030-02-10",
    "ACTIVE",
    true,
  ),
  driver(
    12,
    "Saud Ibrahim Al-Mutairi",
    "سعود إبراهيم المطيري",
    "s.almutairi",
    "2028-09-05",
    "INACTIVE",
    false,
    null,
  ),
  driver(
    13,
    "Turki Abdulrahman Al-Shehri",
    "تركي عبدالرحمن الشهري",
    "t.alshehri",
    "2029-12-01",
    "ACTIVE",
    true,
  ),
  driver(
    14,
    "Majed Hamad Al-Zahrani",
    "ماجد حمد الزهراني",
    "m.alzahrani",
    "2031-04-22",
    "PENDING_ACTIVATION",
    false,
    RIYADH,
  ),
];

/** Mock files are tiny text blobs naming the document -- enough to exercise
 *  View/Download without shipping binary fixtures. A blob: URL where the
 *  runtime has one (browsers block opening data: URLs in a new tab); jsdom
 *  lacks createObjectURL, so tests get a data: URL. */
function mockFileUrl(fileName: string): string {
  const content = `Mock document: ${fileName}`;
  return typeof URL.createObjectURL === "function"
    ? URL.createObjectURL(new Blob([content], { type: "text/plain" }))
    : `data:text/plain;charset=utf-8,${encodeURIComponent(content)}`;
}

function attachment(
  id: string,
  fileName: string,
  mimeType: string,
  sizeBytes: number,
  uploadedAt: string,
): DriverAttachment {
  return {
    id,
    fileName,
    mimeType,
    sizeBytes,
    uploadedAt,
    url: mockFileUrl(fileName),
  };
}

/** Keyed by driver id; drivers missing here have no attachments. */
export const driverAttachmentFixtures: Readonly<
  Record<string, readonly DriverAttachment[]>
> = {
  "driver-001": [
    attachment(
      "att-001",
      "driving-license.pdf",
      "application/pdf",
      482_304,
      "2026-02-03T09:15:00.000Z",
    ),
    attachment(
      "att-002",
      "national-id.jpg",
      "image/jpeg",
      1_204_736,
      "2026-02-03T09:16:00.000Z",
    ),
  ],
  "driver-008": [
    attachment(
      "att-003",
      "driving-license.pdf",
      "application/pdf",
      391_168,
      "2026-04-11T12:40:00.000Z",
    ),
    attachment(
      "att-004",
      "national-id.pdf",
      "application/pdf",
      256_000,
      "2026-04-11T12:41:00.000Z",
    ),
    attachment(
      "att-005",
      "medical-certificate.png",
      "image/png",
      845_312,
      "2026-05-20T07:05:00.000Z",
    ),
  ],
};

export interface ArchivedDriverFixture extends DriverFixture {
  readonly archivedAt: string;
}

/** Four archived drivers (one without a company), archived between March
 *  and August 2026. */
export const archivedDriverFixtures: readonly ArchivedDriverFixture[] = [
  {
    ...driver(
      69,
      "Nasser Ali Al-Qarni",
      "ناصر علي القرني",
      "n.alqarni",
      "2028-06-10",
      "INACTIVE",
      false,
    ),
    archivedAt: "2026-08-14T10:30:00.000Z",
  },
  {
    ...driver(
      66,
      "Bandar Saeed Al-Ghamdi",
      "بندر سعيد الغامدي",
      "b.alghamdi",
      "2027-11-02",
      "ACTIVE",
      false,
      RIYADH,
    ),
    archivedAt: "2026-03-02T08:00:00.000Z",
  },
  {
    ...driver(
      67,
      "Waleed Kamal Hassan",
      "وليد كمال حسن",
      "w.hassan",
      "2029-02-18",
      "PENDING_ACTIVATION",
      false,
      null,
    ),
    archivedAt: "2026-05-21T13:45:00.000Z",
  },
  {
    ...driver(
      68,
      "Rayan Fahd Al-Subaie",
      "ريان فهد السبيعي",
      "r.alsubaie",
      "2026-12-30",
      "ACTIVE",
      false,
    ),
    archivedAt: "2026-07-09T16:20:00.000Z",
  },
];
